import { fetchAlmanac } from "./api.js";
import { BOARD_HEIGHT, BOARD_WIDTH, todayUtcIso } from "./board.js";
import { runBloomEffects } from "./game/bloom-effects.js";
import {
  browserCheckoutDependencies,
  CheckoutController,
  confirmCheckoutReturn,
  pendingCheckoutSelection,
  type CheckoutSelection,
} from "./commerce/checkout.js";
import { GameController, type LocalSolvedArtifact } from "./game/game-controller.js";
import { browserEditionDependencies, EditionComposer, editionTreatmentFor } from "./editions/composer.js";
import { LibraryClient } from "./library/api.js";
import { artifactHashFor, LocalLibrary } from "./library/local-library.js";
import { bootstrapIdentity } from "./identity/bootstrap.js";
import type { LocalSolvedArtifactV2 } from "./library/types.js";
import { loadPublicMaterialProfile } from "./materials/registry.js";
import type { MaterialId, ValidatedMaterialProfile } from "./materials/types.js";
import { wind } from "./materials/wind.js";
import { installPageCleanup } from "./page-lifecycle.js";
import { Atelier, type SanctumDestination } from "./sanctum/atelier.js";
import { SanctumController } from "./sanctum/sanctum-controller.js";

try {
  await bootstrapIdentity();
} catch {
  delete window.CURRENTS_AUTH;
  if (location.pathname === "/auth/callback") history.replaceState(null, "", "/auth/callback");
}

const localLibrary = new LocalLibrary();
const libraryClient = new LibraryClient({
  store: localLibrary,
  token: async () => (window.CURRENTS_AUTH ? window.CURRENTS_AUTH.token() : null),
});

function storedArtifact(bloom: LocalSolvedArtifact): LocalSolvedArtifactV2 {
  const artifact: LocalSolvedArtifactV2 = {
    schemaVersion: 2,
    artifactId: crypto.randomUUID(),
    artifactHash: `sha256:${"0".repeat(64)}`,
    date: bloom.date,
    dayNumber: bloom.dayNumber,
    normalizedStroke: bloom.stroke.map(([x, y]) => ({
      x: Math.max(0, Math.min(1, x / BOARD_WIDTH)),
      y: Math.max(0, Math.min(1, y / BOARD_HEIGHT)),
    })),
    result: { outcome: "bloom", flow: bloom.flow, attempts: bloom.attempts, score: {} },
    share: bloom.share,
    savedLoop: null,
    engineVersion: "2",
    seedVersion: "1",
    landscapeVersion: "1",
    materialId: bloom.materialId,
    materialVersion: bloom.materialVersion,
    primary: true,
    solvedAt: new Date().toISOString(),
  };
  artifact.artifactHash = artifactHashFor(artifact);
  return artifact;
}

function replayArtifact(artifact: LocalSolvedArtifactV2): LocalSolvedArtifact {
  return {
    date: artifact.date,
    dayNumber: artifact.dayNumber,
    stroke: artifact.normalizedStroke.map(({ x, y }) => [x * BOARD_WIDTH, y * BOARD_HEIGHT]),
    outcome: "bloom",
    flow: artifact.result.flow,
    attempts: artifact.result.attempts,
    share: artifact.share,
    materialId: artifact.materialId,
    materialVersion: artifact.materialVersion,
  };
}

function requestedPastDate(): string | null {
  const date = new URLSearchParams(window.location.search).get("date");
  return date !== null && /^\d{4}-\d{2}-\d{2}$/.test(date) && date < todayUtcIso() ? date : null;
}

let game: GameController | null = null;
let sanctum: SanctumController | null = null;
let activeMaterial: ValidatedMaterialProfile = wind;
let materialRequest = 0;
let checkoutFocus: HTMLElement | null = null;

const checkoutConfig = window.CURRENTS_CONFIG;
const checkoutIdentity = window.CURRENTS_AUTH;
const checkoutSignIn = checkoutIdentity?.signIn?.bind(checkoutIdentity);
const checkoutDependencies =
  checkoutConfig?.authBase && checkoutConfig.billingBase && checkoutIdentity && checkoutSignIn
    ? browserCheckoutDependencies({
          apiBase: checkoutConfig.apiBase,
          authBaseUrl: checkoutConfig.authBase,
          billingBaseUrl: checkoutConfig.billingBase,
          ...(checkoutConfig.realmId ? { realmId: checkoutConfig.realmId } : {}),
          identity: { token: () => checkoutIdentity.token(), signIn: checkoutSignIn },
          restoreFocus: () => checkoutFocus?.focus(),
          refreshEntitlements: async () => {
            await libraryClient.sync(true);
          },
        })
    : null;
const checkoutController = checkoutDependencies === null ? null : new CheckoutController(checkoutDependencies);
if (checkoutDependencies !== null && new URL(location.href).searchParams.has("checkout_intent")) {
  void confirmCheckoutReturn(checkoutDependencies).catch(() => undefined);
}

const editionDependencies =
  checkoutConfig && checkoutIdentity
    ? browserEditionDependencies({
        apiBase: checkoutConfig.apiBase,
        token: () => checkoutIdentity.token(),
        ...(checkoutSignIn ? { authorize: () => checkoutSignIn(`${location.pathname}${location.search}#sanctum`) } : {}),
        purchase: async (edition) => {
          checkoutFocus = document.activeElement instanceof HTMLElement ? document.activeElement : null;
          if (checkoutController === null) throw new Error("commerce_unconfigured");
          await checkoutController.keepPrepared({
            intentId: edition.intentId,
            offerVersionId: edition.offerVersionId,
            termsVersion: edition.termsVersion,
          });
        },
      })
    : null;

async function keep(selection: CheckoutSelection): Promise<void> {
  checkoutFocus = document.activeElement instanceof HTMLElement ? document.activeElement : null;
  if (checkoutController === null) throw new Error("commerce_unconfigured");
  await checkoutController.keep(selection);
}

function showCheckoutFailure(selection: CheckoutSelection): void {
  const selector = selection.productKind === "almanac" ? ".almanac-gate p:nth-of-type(2)" : ".material-study-status";
  const status = document.querySelector<HTMLElement>(selector);
  if (status !== null) {
    status.textContent = selection.productKind === "almanac" ? "The Almanac is still preparing." : "This element is still preparing.";
  }
}

async function resumeCheckout(selection: CheckoutSelection): Promise<void> {
  if (selection.productKind === "almanac") {
    await sanctum?.open("almanac", selection.almanacDate);
  } else {
    const material = selection.productKind.startsWith("material:") ? selection.productKind.slice("material:".length) : undefined;
    await sanctum?.open("materials", material === "bundle" ? undefined : material);
  }
  try {
    await keep(selection);
  } catch {
    showCheckoutFailure(selection);
  }
}

async function applyMaterial(id: MaterialId): Promise<MaterialId> {
  const request = ++materialRequest;
  let profile: ValidatedMaterialProfile;
  try {
    profile = await loadPublicMaterialProfile(id);
  } catch {
    profile = wind;
  }
  if (request !== materialRequest) return activeMaterial.id;
  activeMaterial = profile;
  game?.setMaterial(profile);
  return profile.id;
}

async function replayStoredArtifact(artifact: LocalSolvedArtifactV2): Promise<void> {
  const sourceVersion = artifact.materialVersion.startsWith(`${artifact.materialId}@`)
    ? artifact.materialVersion
    : `${artifact.materialId}@${artifact.materialVersion}`;
  const selected = await applyMaterial(artifact.materialId);
  if (selected !== artifact.materialId || activeMaterial.version !== sourceVersion) await applyMaterial("wind");
  await game?.replay(replayArtifact(artifact));
}

const atelierHost = document.getElementById("atelier-continuations");
const atelier =
  atelierHost instanceof HTMLElement
    ? Atelier.mount({
        host: atelierHost,
        sealParent: document.body,
        onContinue: (destination: SanctumDestination, trigger: HTMLElement) => {
          void sanctum?.open(destination, undefined, trigger);
        },
      })
    : null;

const dialog = document.getElementById("sanctum");
if (dialog instanceof HTMLDialogElement) {
  sanctum = SanctumController.mount({
    dialog,
    loadLocal: () => localLibrary.load(),
    capabilities: () => libraryClient.currentCapabilities(),
    fetchAlmanac,
    today: todayUtcIso,
    onReplay: (artifact) => void replayStoredArtifact(artifact),
    onOpenDate: (date) => {
      const target = new URL(window.location.href);
      target.searchParams.set("date", date);
      window.location.assign(target);
    },
    onKeepAlmanac: (date) => keep({ productKind: "almanac", almanacDate: date }),
    selectMaterial: async (id) => applyMaterial(await libraryClient.selectMaterial(id)),
    onKeepMaterial: (id) => keep({ productKind: `material:${id}` }),
    mountEdition: (panel, artifact) =>
      EditionComposer.mount({
        host: panel,
        artifact,
        treatment: editionTreatmentFor(artifact.materialId),
        prepare:
          editionDependencies?.prepare ??
          (async (request) => ({
            editionId: null,
            intentId: null,
            recipeHash: request.recipeHash,
            artifactHash: null,
            renderHash: null,
            proofState: "proof_pending",
            offerVersionId: null,
            offer: null,
            failureCode: "physical_unavailable",
            digitalProofAvailable: false,
          })),
        purchase: editionDependencies?.purchase ?? (async () => Promise.reject(new Error("commerce_unconfigured"))),
        ...(editionDependencies?.loadProof ? { loadProof: editionDependencies.loadProof } : {}),
        ...(editionDependencies?.openDigitalProof ? { openDigitalProof: editionDependencies.openDigitalProof } : {}),
        ...(editionDependencies?.authorize ? { authorize: editionDependencies.authorize } : {}),
      }),
  });
}

function mountGame(): void {
  const canvas = document.getElementById("board");
  if (!(canvas instanceof HTMLCanvasElement) || game !== null) return;
  game = GameController.mount(
    canvas,
    {
      onBloom: (bloom) => {
        runBloomEffects({
          reveal: () => atelier?.afterBloom(bloom),
          persist: () => localLibrary.save(storedArtifact(bloom)),
          sync: () => libraryClient.sync(),
        });
      },
    },
    activeMaterial,
  );
}

async function initialize(): Promise<void> {
  const pending = pendingCheckoutSelection();
  if (pending !== null && checkoutController !== null && window.CURRENTS_AUTH) {
    const token = await window.CURRENTS_AUTH.token();
    if (token) {
      mountGame();
      await libraryClient.sync();
      await applyMaterial(libraryClient.currentMaterial());
      await resumeCheckout(pending);
      return;
    }
  }
  const pastDate = requestedPastDate();
  if (pastDate === null) {
    mountGame();
    void libraryClient.sync().then(() => applyMaterial(libraryClient.currentMaterial()));
    return;
  }
  await libraryClient.sync();
  if (!libraryClient.currentCapabilities().canReplayPast) {
    await sanctum?.open("almanac", pastDate);
    return;
  }
  mountGame();
  await applyMaterial(libraryClient.currentMaterial());
}

void initialize();

installPageCleanup(() => {
  game?.destroy();
  game = null;
  sanctum?.destroy();
  sanctum = null;
});
